/**
 * useDailyProgressData Hook
 *
 * React hook for accessing daily learning progress statistics
 * - Replaces conversation-based calculations with database-driven daily stats
 * - Provides real-time updates through event system (Phase 1)
 * - Maintains cross-conversation persistence
 *
 * Phase 1 Enhancement: Added real-time event subscription for immediate updates
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { dailyProgressService, type DailyProgressStats } from '../services/daily-progress';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import {
  progressEventBus,
  ProgressEventTypeGuards,
  type ProgressEvent
} from '../services/progress-events';

export interface DailyProgressHookResult {
  // Daily statistics
  stats: DailyProgressStats;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Real-time update state (Phase 1)
  isUpdating: boolean;
  lastEventTimestamp: string | null;

  // Actions
  refreshStats: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  triggerOptimisticUpdate: (metadata: { newWords?: number; improvedWords?: number; xpGained?: number }) => void;

  // Derived data
  hasActivity: boolean;
  progressPercentage: number;
}

export function useDailyProgressData(language: string = 'es'): DailyProgressHookResult {
  const { user } = useAuthStore();

  // State management
  const [stats, setStats] = useState<DailyProgressStats>({
    newWordsToday: 0,
    improvedWordsToday: 0,
    dueWords: 0,
    dailyXpEarned: 0,
    lastUpdated: new Date().toISOString()
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase 1: Real-time update tracking
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastEventTimestamp, setLastEventTimestamp] = useState<string | null>(null);

  // Track last successful fetch time for stale-time checks
  const lastFetchRef = useRef<number>(0);

  // 🔧 AGENT FIX: Accumulative optimistic updates that persist until database confirms
  const [pendingChanges, setPendingChanges] = useState({
    newWords: 0,
    improvedWords: 0,
    xpGained: 0
  });

  /**
   * Fetch daily progress stats from the service
   */
  const fetchStats = useCallback(async () => {
    console.log('🎯 fetchStats called:', { userId: user?.id, language });

    if (!user?.id) {
      console.log('❌ No user ID, skipping fetch');
      // Don't set loading to false - we're still waiting for auth
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('🚀 Calling dailyProgressService.fetchDailyProgress...');
      const dailyStats = await dailyProgressService.fetchDailyProgress(user.id, language);
      console.log('✅ Received daily stats:', dailyStats);
      setStats(dailyStats);
      lastFetchRef.current = Date.now();
    } catch (err) {
      console.error('❌ Error fetching daily progress:', err);
      setError('Failed to load daily progress');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, language]);

  /**
   * Refresh stats (called after SRS processing)
   */
  const refreshStats = useCallback(async () => {
    if (!user?.id) return;

    try {
      console.log('🔄 REFRESH STATS: Starting refresh for user:', user.id, 'language:', language);
      const freshStats = await dailyProgressService.refreshAfterSRSUpdate(user.id, language);
      console.log('🔄 REFRESH STATS: Received fresh stats from service:', {
        newWordsToday: freshStats.newWordsToday,
        improvedWordsToday: freshStats.improvedWordsToday,
        dueWords: freshStats.dueWords
      });

      // 🔧 AGENT FIX: Check if database now includes our pending changes
      const currentDisplayStats = {
        newWordsToday: stats.newWordsToday + pendingChanges.newWords,
        improvedWordsToday: stats.improvedWordsToday + pendingChanges.improvedWords
      };

      // If database stats >= our optimistic display, clear pending changes
      if (freshStats.newWordsToday >= currentDisplayStats.newWordsToday &&
          freshStats.improvedWordsToday >= currentDisplayStats.improvedWordsToday) {
        console.log('🔄 REFRESH STATS: Database confirmed pending changes, clearing optimistic updates');
        setPendingChanges({ newWords: 0, improvedWords: 0, xpGained: 0 });
      } else {
        console.log('🔄 REFRESH STATS: Database still processing, keeping pending changes');
      }

      setStats(freshStats);
      console.log('🔄 REFRESH STATS: Stats updated in hook');
    } catch (err) {
      console.error('❌ REFRESH STATS: Error refreshing daily progress:', err);
      // Don't set error state for refresh failures - keep showing current data
    }
  }, [user?.id, language]);

  /**
   * Force refresh data (bypass cache) - for debugging
   */
  const forceRefresh = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const freshStats = await dailyProgressService.forceRefresh(user.id, language);
      setStats(freshStats);
    } catch (err) {
      console.error('Error force refreshing daily progress:', err);
      setError('Failed to force refresh data');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, language]);

  /**
   * Apply optimistic update for immediate UI feedback
   */
  const triggerOptimisticUpdate = useCallback((metadata: {
    newWords?: number;
    improvedWords?: number;
    xpGained?: number;
  }) => {
    setStats(currentStats => {
      const optimisticStats = dailyProgressService.getOptimisticUpdate(currentStats, metadata);
      return optimisticStats;
    });

    // Schedule actual refresh after a short delay
    setTimeout(() => {
      refreshStats();
    }, 1000);
  }, [refreshStats]);

  /**
   * Phase 1: Real-time event subscription for immediate progress updates
   */
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔔 Subscribing to progress events for user:', user.id, 'language:', language);

    const unsubscribeEvents = progressEventBus.subscribe((event: ProgressEvent) => {
      // Only process events for this user and language
      if (!ProgressEventTypeGuards.isUserEvent(event, user.id) ||
          !ProgressEventTypeGuards.isLanguageEvent(event, language)) {
        return;
      }

      console.log('📨 Received progress event:', event.type, event.data);

      // Set updating state for UI feedback
      setIsUpdating(true);
      setLastEventTimestamp(event.data.timestamp);

      // 🔧 AGENT FIX: Apply accumulative optimistic updates that persist until database confirms
      if (ProgressEventTypeGuards.isVocabularyEvent(event)) {
        const newWords = event.type === 'vocabulary-learned' ? 1 : 0;
        const improvedWords = event.type === 'vocabulary-reviewed' ? 1 : 0;
        const xpGained = event.data.xpGained || 0;

        setPendingChanges(prev => {
          const updated = {
            newWords: prev.newWords + newWords,
            improvedWords: prev.improvedWords + improvedWords,
            xpGained: prev.xpGained + xpGained
          };

          console.log('⚡ Applied accumulative optimistic update:', {
            eventData: { newWords, improvedWords, xpGained },
            before: prev,
            after: updated,
            willDisplayAs: {
              newWordsToday: stats.newWordsToday + updated.newWords,
              improvedWordsToday: stats.improvedWordsToday + updated.improvedWords
            }
          });

          return updated;
        });
      }

      // Clear updating state after brief delay
      setTimeout(() => {
        setIsUpdating(false);
      }, 1000);

      // ❌ DISABLED: Automatic database refresh interferes with explicit timing control
      // The parent component (index.tsx) now explicitly calls refreshStats via callback
      // AFTER messages render, ensuring proper visual sequencing regardless of API speed.
      //
      // This prevents race conditions where stats update before messages appear on slow networks.
      // The optimistic updates above still provide instant feedback, but the database refresh
      // is now coordinated with message rendering via requestAnimationFrame in the parent.
      //
      // Schedule database sync for accuracy - batch vocabulary events to avoid spam
      // if (event.type === 'daily-stats-updated') {
      //   setTimeout(() => {
      //     refreshStats();
      //   }, 500);
      // } else if (event.type === 'vocabulary-learned' || event.type === 'vocabulary-reviewed') {
      //   // 🔧 CRITICAL FIX: Refetch database stats after vocabulary events
      //   console.log('🔄 Scheduling database refresh for vocabulary event:', event.type);
      //   setTimeout(() => {
      //     console.log('🔄 Executing database refresh for updated stats - STARTING');
      //     refreshStats().then(() => {
      //       console.log('🔄 Database refresh completed successfully');
      //     }).catch((error) => {
      //       console.error('❌ Database refresh failed:', error);
      //     });
      //   }, 500); // 🔧 AGENT FIX: Reduced from 2000ms - faster refresh prevents optimistic update overwrite
      // }
    });

    return unsubscribeEvents;
  }, [user?.id, language, refreshStats]);

  /**
   * Subscribe to daily progress service updates and handle visibility changes
   */
  useEffect(() => {
    // Only fetch if user is available
    if (!user?.id) {
      console.log('⏳ Waiting for user authentication before fetching stats');
      setIsLoading(true); // Keep loading state while waiting for auth
      return;
    }

    console.log('✅ User authenticated, fetching initial stats');
    // Initial fetch
    fetchStats();

    // Subscribe to updates from the service
    const unsubscribeService = dailyProgressService.subscribe((newStats) => {
      setStats(newStats);
      setIsUpdating(false); // Ensure updating state is cleared
    });

    // Handle visibility change - refresh only if data is stale (>30s old)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const staleDuration = 30_000; // 30 seconds
        if (Date.now() - lastFetchRef.current > staleDuration) {
          fetchStats();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      unsubscribeService();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchStats, user?.id]); // Add user?.id as dependency

  /**
   * Realtime auto-update: refetch the counts when SRS cards actually land in
   * the DB. Cards are created server-side by message-processor-v3 during chat,
   * so the parent's post-render refreshStats() can fire before the rows commit
   * (a race it can't win). Subscribing to the card tables makes the widget
   * (e.g. the "due today" count) update on its own once cards exist — no
   * manual reload. Inserts arrive in bursts, so we debounce to a single refetch.
   */
  useEffect(() => {
    if (!user?.id) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        refreshStats();
      }, 700);
    };

    const channel = supabase
      .channel(`daily-progress-cards-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'srs_cards_minimal', filter: `user_id=eq.${user.id}` },
        scheduleRefresh
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [user?.id, refreshStats]);

  /**
   * Derived values
   */
  const hasActivity = useMemo(() => {
    return stats.newWordsToday > 0 || stats.improvedWordsToday > 0;
  }, [stats]);

  const progressPercentage = useMemo(() => {
    // Calculate progress toward daily goals
    const dailyGoal = 20; // Default daily goal (can be made configurable)
    const totalProgress = stats.newWordsToday + stats.improvedWordsToday;
    return Math.min(100, Math.round((totalProgress / dailyGoal) * 100));
  }, [stats]);

  return {
    stats,
    isLoading,
    error,
    // Phase 1: Real-time update state
    isUpdating,
    lastEventTimestamp,
    refreshStats,
    forceRefresh,
    triggerOptimisticUpdate,
    hasActivity,
    progressPercentage
  };
}

/**
 * Helper hook for formatting daily progress stats
 */
export function useFormattedDailyStats(stats: DailyProgressStats) {
  return useMemo(() => ({
    newWords: {
      value: stats.newWordsToday,
      label: stats.newWordsToday === 1 ? 'New Word Today' : 'New Words Today',
      color: 'text-chart-1'
    },
    improved: {
      value: stats.improvedWordsToday,
      label: stats.improvedWordsToday === 1 ? 'Word Reviewed' : 'Words Reviewed',
      color: 'text-chart-2'
    },
    due: {
      value: stats.dueWords,
      label: stats.dueWords === 1 ? 'Word Due' : 'Words Due',
      color: 'text-chart-3'
    },
    xp: {
      value: stats.dailyXpEarned,
      label: 'XP Earned Today'
    }
  }), [stats]);
}